import { createReducer, on } from '@ngrx/store';
import { MembershipLevel } from '../models/membership-level.model';
import { MembershipActions } from './membership.actions';

export const membershipFeatureKey = 'membership';

export interface MembershipState {
  levels: MembershipLevel[];
  levelsLoading: boolean;
  /** Property id the current `levels` belong to (current property context). */
  loadedPropertyId: string | null;
}

export const initialState: MembershipState = {
  levels: [],
  levelsLoading: false,
  loadedPropertyId: null,
};

function upsertLevel(levels: MembershipLevel[], level: MembershipLevel): MembershipLevel[] {
  const idx = levels.findIndex(l => l.membershipCategoryId === level.membershipCategoryId);
  const next =
    idx >= 0
      ? [...levels.slice(0, idx), level, ...levels.slice(idx + 1)]
      : [...levels, level];
  return [...next].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export const reducer = createReducer(
  initialState,

  on(MembershipActions.clearLevels, (): MembershipState => ({ ...initialState })),

  on(MembershipActions.loadLevels, (state, { propertyId }): MembershipState => ({
    ...state,
    levelsLoading: true,
    loadedPropertyId: propertyId,
    levels: state.loadedPropertyId === propertyId ? state.levels : [],
  })),

  on(MembershipActions.loadLevelsSuccess, (state, { propertyId, levels }): MembershipState => ({
    ...state,
    levelsLoading: false,
    levels,
    loadedPropertyId: propertyId,
  })),

  on(MembershipActions.loadLevelsFailure, (state): MembershipState => ({
    ...state,
    levelsLoading: false,
  })),

  on(MembershipActions.createLevelSuccess, (state, { propertyId, level }): MembershipState => {
    if (state.loadedPropertyId !== propertyId) {
      return state;
    }
    return {
      ...state,
      levels: upsertLevel(state.levels, level),
    };
  }),

  on(MembershipActions.updateLevelSuccess, (state, { propertyId, level }): MembershipState => {
    if (state.loadedPropertyId !== propertyId) {
      return state;
    }
    return {
      ...state,
      levels: upsertLevel(state.levels, level),
    };
  }),

  on(MembershipActions.deleteLevelSuccess, (state, { propertyId, membershipCategoryId }): MembershipState => {
    if (state.loadedPropertyId !== propertyId) {
      return state;
    }
    return {
      ...state,
      levels: state.levels.filter(l => l.membershipCategoryId !== membershipCategoryId),
    };
  }),
);
